// Round 272 — consolidated raw-fetch wrapper for admin features whose
// endpoints are not yet in openapi.yaml. Replaces 6+ duplicated copies of
// `adminFetch` / `systemFetch` / `whatsappFetch` / `testModeFetch` across
// src/features/admin/*. All errors surface as ApiError so the UI gets a
// uniform `{ status, body }` contract.
import { ApiError } from "@/lib/api/error";
import { withCsrf } from "./csrf";
import { withRenewRetry } from "./middleware";

const JSON_HEADERS: Readonly<Record<string, string>> = {
  "Content-Type": "application/json",
};

async function readErrorBody(res: Response): Promise<unknown> {
  // Best-effort: try JSON, fall back to text. Body is consumed once.
  try {
    return await res.clone().json();
  } catch {
    try {
      return await res.text();
    } catch {
      return null;
    }
  }
}

function userFacingValidationDetail(
  status: number,
  body: unknown,
): string | null {
  if (status !== 422 || !body || typeof body !== "object") return null;
  const detail = (body as Record<string, unknown>)["detail"];
  return typeof detail === "string" && detail.trim() ? detail : null;
}

function safeAdminErrorMessage(
  label: string | undefined,
  status: number,
  body: unknown,
): string {
  const detail = userFacingValidationDetail(status, body);
  if (detail) return detail;
  if (label === "fetchAdminAnomalies")
    return `${label} failed status=${status}`;
  return `${label ?? "adminFetch"} failed`;
}

/**
 * Fetch a JSON resource using cookie auth and throw ApiError on non-2xx.
 *
 * The optional `label` is kept for call-site compatibility; failures are
 * structured as ApiError so UI code can format them without leaking internals.
 *
 * @example
 *   const list = await adminFetch<AdminPlanList>("/api/v2/admin/plans?limit=50", undefined, "fetchPlans");
 */
export async function adminFetch<T>(
  url: string,
  opts?: RequestInit,
  label?: string,
): Promise<T> {
  const res = await withRenewRetry(withCsrf(fetch))(url, {
    credentials: "include",
    ...opts,
  });
  if (!res.ok) {
    const body = await readErrorBody(res);
    // NEVER console.log request opts here: headers may include x-csrf-token.
    throw new ApiError(
      res.status,
      body,
      safeAdminErrorMessage(label, res.status, body),
    );
  }
  if (res.status === 204) return undefined as T;
  return readSuccessBody<T>(res);
}

async function readSuccessBody<T>(res: Response): Promise<T> {
  if (typeof res.text !== "function") {
    return (await res.json()) as T;
  }
  const text = await res.text();
  if (!text) return undefined as T;
  return JSON.parse(text) as T;
}

/**
 * Build a JSON-body RequestInit. Consolidates jsonOpts/jsonPost/jsonPatch.
 *
 * @example
 *   await adminFetch(url, jsonRequest("POST", { foo: 1 }));
 */
export function jsonRequest(
  method: string,
  body: unknown,
  opts: RequestInit = {},
): RequestInit {
  const headers = new Headers(opts.headers);
  for (const [key, value] of Object.entries(JSON_HEADERS)) {
    if (!headers.has(key)) headers.set(key, value);
  }
  return {
    ...opts,
    method,
    headers,
    body: JSON.stringify(body),
  };
}
